import React, { forwardRef, useEffect, useState } from "react";
import Skeleton from '../../components/Skeleton';
import DatePicker from "react-datepicker";
import { useIntl } from "react-intl";
import { api, getPlanEnterprise } from '../../api';

import CompanyUsers from './components/CompanyUsers';
import Graph from './components/Graph';
import './styles.scss';

const CompanyReports = ({ setError }) => {
  const { formatMessage } = useIntl();
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState({});
  const [graph, setGraph] = useState({});
  const [graphDate, setGraphDate] = useState(new Date().getTime());
  const [usersData, setUsersData] = useState([]);
  const [usersList, setUsersList] = useState([]);

  useEffect(() => {
    api.get(`/b3_consultas/query_graph_enterprise?date=${graphDate}`)
      .then(({ data: response }) => {
        setGraph(response.data);
      })
      .catch(err => {
        setError(formatMessage({ id: "erro-api" }));
      })
  }, [graphDate, setError, formatMessage])

  useEffect(() => {
    api.get(`/b3_consultas/get_users_report_enterprise`)
      .then(({ data: response }) => {
        const firstChilds = response.result.B3PessoaJuridicaAutorizado.map(pj => ({
          id: pj.PessoaFisica.id,
          nome: pj.PessoaFisica.nome, 
          consultas_feitas: pj.PessoaFisica.Consumo.qtd_consultas,
          level: 1,
        }));

        let remainChilds = [];
        if (response.result.Children.length > 0) {
          const childrens = response.result.Children.map(child => {
            const obj = {
              id: child.id,
              nome: child.nome_fantasia, 
              consultas_feitas: child.Consumo.qtd_consultas,
              level: 1,
              childs: [...child.B3PessoaJuridicaAutorizado.map(pj => ({
                id: pj.PessoaFisica.id,
                nome: pj.PessoaFisica.nome, 
                consultas_feitas: pj.PessoaFisica.Consumo.qtd_consultas,
                level: 2,
              }))]
            }

            if (child.Children2.length > 0) {
              const innerChildren = child.Children2.map(innerChild => ({
                id: innerChild.id,
                nome: innerChild.nome_fantasia, 
                consultas_feitas: innerChild.Consumo.qtd_consultas,
                level: 3,
                childs: innerChild.B3PessoaJuridicaAutorizado.map(innerPj => ({
                  id: innerPj.PessoaFisica.id,
                  nome: innerPj.PessoaFisica.nome, 
                  consultas_feitas: innerPj.PessoaFisica.Consumo.qtd_consultas,
                  level: 3,
                }))
              }))

              obj.childs.push(innerChildren);
            }           
            
            return obj;
          })
          
          childrens.forEach(obj => {
            if (Array.isArray(obj)) {
              remainChilds = [...remainChilds, ...obj];
              return;
            }
  
            remainChilds = [...remainChilds, obj];
          })
        }

        const allChilds = [...firstChilds, ...remainChilds];

        const data = [{
          id: response.result.PessoaJuridica.id,
          nome: response.result.PessoaJuridica.nome_fantasia,
          consultas_feitas: response.result.PessoaJuridica.Consumo.qtd_consultas,
          childs: allChilds
        }];

        setUsersData(data);
      })
      .catch(err => {
        setError(formatMessage({ id: "erro-api" }));
      })
  }, [graphDate, setError, formatMessage])

  useEffect(() => {
    getPlanEnterprise()
      .then(({ data: response }) => {
        const result = response.result;

        setData({
          plan: {
            name: result.B3Plano.nome,
            totalQueries: result.B3Plano.qtd_consultas,
            tolerance: result.B3Plano.tolerancia,
            chargeDay: result.B3PessoaJuridicaPlano.dia_bilhetagem,
          },
          queries: {
            done: result.Consumo.qtd_consumida,
            remaining: result.B3Plano.qtd_consultas - result.Consumo.qtd_consumida,
            available: result.Consumo.has_consulta_disponivel
          },
          account: {
            master: result.PessoaJuridica.nome_fantasia,
            direct: result.PessoaJuridicaDireta.nome_fantasia
          }
        });

        setIsLoading(false);
      })
      .catch(err => {
        setError(formatMessage({ id: "erro-api" }));
      })

    api.get('/b3_consultas/get_users_list_enterprise')
      .then(({ data: response  }) => {
        setUsersList(response.result);
      })
      .catch(() => {
        setError(formatMessage({ id: "erro-api" }));
      })
  }, [formatMessage, setError])

  const CustomDateInput = forwardRef(
    ({ value, onClick }, ref) => (
      <button className="custom-date-input" onClick={onClick} ref={ref}>
        {value}
      </button>
    )
  )

  return ( 
    <>
      {isLoading ? (
        <>
          <h1>Relatórios</h1>
          <p className="header-subtitle">para a conta empresarial Sicoob Norte</p>
          
          <div className="container-dash">
            <div className="dash-inner">
              <div className="dash-item">
                <h2><Skeleton width="40" /></h2>
                <span>{formatMessage({ id: 'consultas-feitas' })}</span>
              </div>
              <div className="dash-item">
                <h2><Skeleton width="40" /></h2>
                <span>{formatMessage({ id: 'consultas-restantes' })}</span>
              </div>
              <div className="dash-item">
                <h2><Skeleton width="40" /></h2>
                <span>{formatMessage({ id: 'total-usuarios' })}</span>
              </div>
            </div>
          </div>
    
          <div className="content-box content-box-reports">
            <h4>{formatMessage({ id: 'informacoes' })}</h4>
    
            <div>
              <p>{formatMessage({ id: 'conta-pj-master' })}: <strong><Skeleton width="40" /></strong></p>
              <p>{formatMessage({ id: 'conta-pj-direta' })}: <strong><Skeleton width="40" /></strong></p>
              <p>{formatMessage({ id: 'plano-atual' })}: <Skeleton width="40" /></p>
              <p>{formatMessage({ id: 'dia-bilhetagem' })}: <Skeleton width="40" /></p>
            </div>
          </div>
        </>
      ) :(
        <>
          <h1>Relatórios</h1>
          <p className="header-subtitle">para a conta empresarial {data.account.master}</p>
          
          <div className="container-dash">
            <div className="dash-inner">
              <div className="dash-item">
                <h2 data-testid="consultas-feitas">{data.queries.done}</h2>
                <span>{formatMessage({ id: 'consultas-feitas' })}</span>
              </div>
              <div className="dash-item">
                <h2 data-testid="consultas-restantes">{data.queries.remaining}</h2>
                <span>{formatMessage({ id: 'consultas-restantes' })}</span>
              </div>
              <div className="dash-item">
                <h2 data-testid="total-usuarios">{usersList.length}</h2>
                <span>{formatMessage({ id: 'total-usuarios' })}</span>
              </div>
            </div>
          </div>
    
          {!data.queries.available && (
            <div className="alert">
              <p>Você ultrapassou a sua cota de consultas, por favor contacte a equipe Terras.</p>
            </div>
          )}
    
          <div className="content-box content-box-reports">
            <h4>{formatMessage({ id: 'informacoes' })}</h4>
    
            <div>
              <p>{formatMessage({ id: 'conta-pj-master' })}: <strong data-testid="account-master">{data.account.master}</strong></p>
              <p>{formatMessage({ id: 'conta-pj-direta' })}: <strong data-testid="account-direct">{data.account.direct}</strong></p>
              <p>{formatMessage({ id: 'plano-atual' })}: &nbsp; 
                <strong>{data.plan.name} ({data.plan.totalQueries} {formatMessage({ id: 'consultas-mensais' })},&nbsp; 
                  {Number(data.plan.tolerance) * 100}% {formatMessage({ id: 'tolerancia' })})
                </strong>
              </p>
              <p>{formatMessage({ id: 'dia-bilhetagem' })}: <strong data-testid="charge-day">{data.plan.chargeDay}</strong></p>
            </div>
          </div>
    
          <div className="content-box">
            <div className="historico-consultas-header">
              <h4>{formatMessage({ id: 'historico-consultas' })}</h4>

              <div>
                <DatePicker 
                  selected={graphDate}
                  dateFormat="MM/yyyy"
                  onChange={date => setGraphDate(date.getTime())}
                  customInput={<CustomDateInput />}
                  showMonthYearPicker
                /> 
              </div>
            </div>
    
            <div>
              <Graph data={graph} />
            </div>
          </div>
    
    
          <div className="content-box">
            <h4 style={{ marginBottom: 20 }}>{formatMessage({ id: 'usuarios' })}</h4>
    
            <div>
              <CompanyUsers objectsToRender={usersData} />
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default CompanyReports;
